import { useMemo } from 'react';

import { useAppSelector } from '~/store';

import type { EntitlementResolution, TrialInfo } from './interfaces';
import { selectResolution } from './selectors';

export const TRIAL_WIDGET_FLAG = 'trial_awareness_widget';

const NO_TRIAL: TrialInfo = {
  isTrial: false,
  daysRemaining: 0,
  trialEndsAt: null,
  expired: false,
};

function fromResolution(resolution: EntitlementResolution | null): TrialInfo {
  const trial = resolution?.trial;
  if (!trial) return NO_TRIAL;
  return {
    isTrial: trial.isActive,
    daysRemaining: trial.daysRemaining,
    trialEndsAt: trial.endsAt,
    expired: !trial.isActive,
  };
}

export function useTrialInfo(): TrialInfo {
  const resolution = useAppSelector(selectResolution);
  return useMemo(() => fromResolution(resolution), [resolution?.trial]);
}

export function useTrialWidgetEnabled(): boolean {
  return useAppSelector(
    ({ entitlements }) =>
      entitlements.resolution?.flags?.[TRIAL_WIDGET_FLAG] !== false,
  );
}

export type AwarenessState =
  | 'trial_active'
  | 'trial_ending'
  | 'trial_ended'
  | 'cancelled'
  | 'expired'
  | 'no_plan';

export interface BillingAwareness {
  state: AwarenessState;
  daysRemaining: number;
}

const MS_PER_DAY = 86_400_000;

function daysUntil(iso: string): number {
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return 0;
  return Math.max(Math.floor(end / MS_PER_DAY) - Math.floor(Date.now() / MS_PER_DAY), 0);
}

function deriveAwareness(
  resolution: EntitlementResolution | null,
): BillingAwareness | null {
  if (!resolution) return null;
  const subscription = resolution.subscription;
  const status = subscription?.status;
  const isPremiumTier = resolution.planTier !== 'free';

  if (
    isPremiumTier &&
    (status === 'active' || status === 'past_due' || status === 'manual_grant')
  ) {
    return null;
  }

  const trial = resolution.trial;
  if (trial?.isActive) {
    return {
      state: trial.daysRemaining <= 2 ? 'trial_ending' : 'trial_active',
      daysRemaining: trial.daysRemaining,
    };
  }
  if (status === 'trialing' && subscription?.trialEndsAt) {
    const days = daysUntil(subscription.trialEndsAt);
    if (new Date(subscription.trialEndsAt).getTime() > Date.now()) {
      return {
        state: days <= 2 ? 'trial_ending' : 'trial_active',
        daysRemaining: days,
      };
    }
  }
  if (status === 'cancelled') return { state: 'cancelled', daysRemaining: 0 };
  if (status === 'expired') return { state: 'expired', daysRemaining: 0 };
  if (status === 'trialing') return { state: 'trial_ended', daysRemaining: 0 };
  return { state: 'no_plan', daysRemaining: 0 };
}

export function useBillingAwareness(): BillingAwareness | null {
  const resolution = useAppSelector(selectResolution);
  return useMemo(() => deriveAwareness(resolution), [resolution]);
}
